import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { dbUserAndHub } from "../../config/api";
import { SUBSCRIPTION_CONFIG } from "../../config/zinihubConfig";
import { encryptUserAndHubPayload } from "../../utils/encryption";
import { getCashfree } from "./CashfreeSdk";
import {
  App as AntApp,
  Card,
  Select,
  Spin,
  Button,
  Space,
  Typography,
  Empty,
  Modal,
  Radio,
  Popconfirm,
  Progress,
  Alert,
} from "antd";
import {
  CrownOutlined,
  StopOutlined,
  ArrowDownOutlined,
  GiftOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import { MdOutlineAutoMode } from "react-icons/md";
import {
  getHubProfiles,
  getSubscriptionForHub,
  getHubDisplayName,
  formatSubscriptionDate,
  formatLastAccessDate,
  getUpgradeOptions,
  getDowngradeOptions,
  VALIDITY,
  DISCOUNT_OFF_SUFFIX,
  isUpgradeDisabled,
  isDowngradeDisabled,
  getProgressBarState,
  shouldShowUpgradeOptions,
  formatSubscriptionRemainingDaysLabel,
  getUpgradeWindowBlockedDescription,
} from "./SubscriptionHelpers";
import {
  deriveHubSubscriptionState,
  SUBSCRIPTION_STATE,
} from "../../utils/subscriptionState";
import { buildProfileSubscriptionSummary } from "../../utils/subscriptionDisplay";
import { SUBSCRIPTION_PROFILE_LABELS } from "../../labels/subscriptionProfileLabels";
import {
  calculatePlanBillingAmount,
  getPlanBillingCycleIds,
  planSupportsBillingCycle,
} from "../../services/subscriptionCatalog";
import { SUBSCRIPTION_ONBOARDING_LABELS } from "../../labels/subscriptionOnboardingLabels";
import { SUBSCRIPTION_PRICING_LABELS } from "../../labels/subscriptionPricingLabels";
import { GUIDED_JOURNEY_TARGET } from "../../config/guidedJourneyConfig";
import styles from "./Subscription.module.css";
import PageContainer from "../../components/PageContainer/PageContainer";
import {
    buildActivateTrialActionPayload,
    buildCancelActionPayload,
    buildDowngradeActionPayload,
    buildPaidCheckoutActionPayload,
    resolveActivationDefaultPlanId,
    validateActivateTrialSelection,
    validatePaidCheckoutSelection,
} from "./subscriptionActionPayloads";

const { Title, Text } = Typography;

const formatPlanLabel = (plan) => plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : "";
const formatCatalogPrice = (amount, currency = "INR") =>
    amount == null
        ? SUBSCRIPTION_PRICING_LABELS.notAvailable
        : new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(Number(amount || 0));
const isFreeTrialPlan = (plan) => plan?.trial?.enabled === true && Number(plan?.monthlyPrice) === 0;
const calculateUndiscountedAmount = (plan, cycle) => {
    const monthlyPrice = Number(plan?.monthlyPrice);
    const months = Number(cycle?.months || 1);
    if (!Number.isFinite(monthlyPrice) || !Number.isFinite(months) || months <= 0) return null;
    return Math.round(monthlyPrice * months);
};
const calculateEffectiveMonthlyAmount = (amount, cycle) => {
    const months = Number(cycle?.months || 1);
    if (!Number.isFinite(Number(amount)) || !Number.isFinite(months) || months <= 0) return null;
    return Math.round(Number(amount) / months);
};

const isBillingCycleSupported = (catalog, plans, cycleId, selectedPlanId) => {
    if (selectedPlanId) {
        const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);
        return selectedPlan ? planSupportsBillingCycle(catalog, selectedPlan, cycleId) : false;
    }
    return plans.some((plan) => planSupportsBillingCycle(catalog, plan, cycleId));
};

const BillingCycleToggle = ({
    value,
    onChange,
    options,
    billingCycleById,
    catalog,
    plans,
    selectedPlanId,
    disabled = false,
}) => (
    <Radio.Group
        value={value}
        onChange={(event) => onChange(event.target.value)}
        optionType="button"
        buttonStyle="solid"
        disabled={disabled}
        className={styles.cycleToggle}
    >
        {options.map((option) => {
            const cycle = billingCycleById.get(option.value);
            const discountPercent = Number(cycle?.discountPercent || 0);
            const cycleDisabled = disabled || !isBillingCycleSupported(catalog, plans, option.value, selectedPlanId);
            return (
                <Radio.Button
                    key={option.value}
                    value={option.value}
                    disabled={cycleDisabled}
                    className={styles.cycleToggleButton}
                >
                    <span>{option.label}</span>
                    {discountPercent > 0 && (
                        <span className={styles.cycleDiscountChip}>
                            {discountPercent}% {DISCOUNT_OFF_SUFFIX}
                        </span>
                    )}
                </Radio.Button>
            );
        })}
    </Radio.Group>
);

const CompactPlanCard = ({
    plan,
    cycle,
    catalog,
    currency,
    selected = false,
    isCurrent = false,
    showTrialHighlight = false,
    selectable = false,
    disabled = false,
    onSelect,
}) => {
    const freeTrialPlan = isFreeTrialPlan(plan);
    const supportsCycle = cycle && catalog ? planSupportsBillingCycle(catalog, plan, cycle.id) : Boolean(cycle);
    const amount = cycle && supportsCycle ? calculatePlanBillingAmount(plan, cycle) : null;
    const originalAmount = cycle ? calculateUndiscountedAmount(plan, cycle) : null;
    const effectiveMonthly = calculateEffectiveMonthlyAmount(amount, cycle);
    const savings = Number(originalAmount || 0) - Number(amount || 0);
    const trialDays = Number(plan?.trial?.days || 0);

    const cardClassName = [
        styles.compactPlanCard,
        selected ? styles.compactPlanCardSelected : "",
        isCurrent ? styles.compactPlanCardCurrent : "",
        selectable ? styles.compactPlanCardSelectable : "",
        !supportsCycle ? styles.compactPlanCardDisabled : "",
    ].filter(Boolean).join(" ");

    const content = (
        <>
            <div className={styles.compactPlanCardHead}>
                <strong className={styles.compactPlanName}>{plan.name || formatPlanLabel(plan.id)}</strong>
                <div className={styles.compactPlanBadges}>
                    {isCurrent && (
                        <span className={styles.currentBadge}>{SUBSCRIPTION_PRICING_LABELS.currentPlan}</span>
                    )}
                    {showTrialHighlight && plan.trial?.enabled && (
                        <span className={styles.trialAvailableChip}>{SUBSCRIPTION_PRICING_LABELS.trialAvailableChip}</span>
                    )}
                </div>
            </div>
            {freeTrialPlan && showTrialHighlight && supportsCycle ? (
                <>
                    <div className={styles.compactPlanAmount}>{SUBSCRIPTION_PRICING_LABELS.freeTrial}</div>
                    <div className={styles.compactPlanMeta}>{SUBSCRIPTION_PRICING_LABELS.trialPeriod(trialDays)}</div>
                    <div className={styles.compactPlanHint}>{SUBSCRIPTION_PRICING_LABELS.noCreditCard}</div>
                </>
            ) : !supportsCycle ? (
                <>
                    <div className={styles.compactPlanAmount}>{SUBSCRIPTION_PRICING_LABELS.notAvailable}</div>
                    <div className={styles.compactPlanMeta}>{SUBSCRIPTION_PRICING_LABELS.billedMonthly}</div>
                </>
            ) : (
                <>
                    <div className={styles.compactPlanAmount}>
                        {formatCatalogPrice(amount, currency)}
                        {savings > 0 && (
                            <small>{formatCatalogPrice(originalAmount, currency)}</small>
                        )}
                    </div>
                    <div className={styles.compactPlanMeta}>
                        {cycle?.months > 1
                            ? SUBSCRIPTION_PRICING_LABELS.effectiveMonthly(formatCatalogPrice(effectiveMonthly, currency))
                            : SUBSCRIPTION_PRICING_LABELS.billedMonthly}
                    </div>
                    {savings > 0 && (
                        <div className={styles.compactPlanSavings}>
                            {SUBSCRIPTION_PRICING_LABELS.saveAmount(formatCatalogPrice(savings, currency))}
                        </div>
                    )}
                </>
            )}
        </>
    );

    if (!selectable) {
        return <div className={cardClassName}>{content}</div>;
    }

    return (
        <button
            type="button"
            className={cardClassName}
            disabled={disabled || !supportsCycle}
            onClick={onSelect}
        >
            {content}
        </button>
    );
};

const CompactPricingPicker = ({
    catalog,
    plans,
    cycle,
    onCycleChange,
    cycleOptions,
    billingCycleById,
    selectedPlanId,
    onPlanSelect,
    currentPlanId,
    showTrialHighlight,
    selectable = false,
    disabled = false,
    showCycleSelector = true,
    showPlanLabel = true,
    restrictCyclesToSelectedPlan = false,
}) => {
    const selectedCycle = billingCycleById.get(cycle);
    const cycleSelectedPlanId = restrictCyclesToSelectedPlan ? selectedPlanId : null;

    return (
        <div className={styles.compactPricingPicker}>
            {showCycleSelector && cycleOptions.length > 0 && (
                <div className={styles.compactPricingStep}>
                    <Text className={styles.compactPricingStepLabel}>
                        {SUBSCRIPTION_PRICING_LABELS.chooseBillingCycle}
                    </Text>
                    <BillingCycleToggle
                        value={cycle}
                        onChange={onCycleChange}
                        options={cycleOptions}
                        billingCycleById={billingCycleById}
                        catalog={catalog}
                        plans={plans}
                        selectedPlanId={cycleSelectedPlanId}
                        disabled={disabled}
                    />
                </div>
            )}
            {showPlanLabel && (
                <div className={styles.compactPricingStep}>
                    <Text className={styles.compactPricingStepLabel}>
                        {SUBSCRIPTION_PRICING_LABELS.selectPlan}
                    </Text>
                    <div className={styles.compactPlanGrid}>
                        {plans.map((plan) => (
                            <CompactPlanCard
                                key={plan.id}
                                plan={plan}
                                cycle={selectedCycle}
                                catalog={catalog}
                                currency={catalog?.currency}
                                selected={selectedPlanId === plan.id}
                                isCurrent={currentPlanId === plan.id}
                                showTrialHighlight={showTrialHighlight && plan.trial?.enabled}
                                selectable={selectable}
                                disabled={disabled}
                                onSelect={() => onPlanSelect?.(plan.id)}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const TrialHighlightBanner = ({
    plan,
    onActivateTrial,
    loading = false,
    disabled = false,
}) => {
    const trialDays = Number(plan?.trial?.days || 0);
    return (
        <div className={styles.trialBanner}>
            <div className={styles.trialBannerMain}>
                <div className={styles.trialBannerIcon} aria-hidden="true">
                    <GiftOutlined />
                </div>
                <div className={styles.trialBannerContent}>
                    <div className={styles.trialBannerHead}>
                        <strong>{plan?.name || formatPlanLabel(plan?.id)} Plan</strong>
                        <span className={styles.trialAvailableChip}>{SUBSCRIPTION_PRICING_LABELS.trialAvailableChip}</span>
                    </div>
                    <div className={styles.trialBannerSubtitle}>
                        {SUBSCRIPTION_ONBOARDING_LABELS.trialBannerSubtitle(trialDays)}
                    </div>
                    <ul className={styles.trialBannerList}>
                        <li>{SUBSCRIPTION_ONBOARDING_LABELS.trialBannerExplore}</li>
                        <li>{SUBSCRIPTION_ONBOARDING_LABELS.trialBannerNoCard}</li>
                        <li>{SUBSCRIPTION_ONBOARDING_LABELS.trialBannerUpgrade}</li>
                    </ul>
                </div>
            </div>
            <div className={styles.trialBannerAction}>
                <Button
                    type="primary"
                    size="large"
                    icon={<GiftOutlined />}
                    loading={loading}
                    disabled={disabled}
                    onClick={onActivateTrial}
                    className={styles.trialBannerCta}
                >
                    {loading
                        ? SUBSCRIPTION_ONBOARDING_LABELS.activatingTrial
                        : SUBSCRIPTION_ONBOARDING_LABELS.activateFreeTrial}
                </Button>
                <Text className={styles.trialBannerCtaHint}>{SUBSCRIPTION_PRICING_LABELS.noCreditCard}</Text>
            </div>
        </div>
    );
};

const ActivationReviewSection = ({
    selectedPlan,
    cycle,
    catalog,
    billingCycleById,
    activationShowsTrialCta,
    trialDays,
    isBusy,
    catalogError,
    subscriptionCatalog,
    onActivateTrial,
    onSubscribe,
    subscriptionActionLoading,
}) => {
    const selectedCycle = billingCycleById.get(cycle);
    const amount = selectedPlan && selectedCycle
        ? calculatePlanBillingAmount(selectedPlan, selectedCycle)
        : null;
    const formattedAmount = formatCatalogPrice(amount, catalog?.currency);

    return (
        <div className={styles.reviewSection}>
            <Text className={styles.compactPricingStepLabel}>
                {SUBSCRIPTION_PRICING_LABELS.reviewAndContinue}
            </Text>
            <div className={styles.reviewSummary}>
                <div className={styles.reviewSummaryMain}>
                    {activationShowsTrialCta ? (
                        <>
                            <strong>{SUBSCRIPTION_ONBOARDING_LABELS.reviewTrialSelection(selectedPlan?.name, trialDays)}</strong>
                            <span>{SUBSCRIPTION_ONBOARDING_LABELS.reviewNoPayment}</span>
                        </>
                    ) : (
                        <strong>
                            {SUBSCRIPTION_ONBOARDING_LABELS.reviewPaidSelection(
                                selectedPlan?.name,
                                selectedCycle?.label,
                                formattedAmount
                            )}
                        </strong>
                    )}
                </div>
                {activationShowsTrialCta && (
                    <ul className={styles.reviewHighlights}>
                        <li>{SUBSCRIPTION_ONBOARDING_LABELS.reviewFullAccess(selectedPlan?.name)}</li>
                        <li>{SUBSCRIPTION_ONBOARDING_LABELS.reviewTrialDuration(trialDays)}</li>
                        <li>{SUBSCRIPTION_ONBOARDING_LABELS.reviewUpgradeAnytime}</li>
                    </ul>
                )}
            </div>
            {activationShowsTrialCta ? (
                <Button
                    id={GUIDED_JOURNEY_TARGET.SUBSCRIPTION_TRIAL}
                    type="primary"
                    size="large"
                    icon={<GiftOutlined />}
                    loading={subscriptionActionLoading === "activateTrial"}
                    disabled={!subscriptionCatalog || Boolean(catalogError) || isBusy}
                    onClick={() => onActivateTrial()}
                    className={styles.reviewCta}
                >
                    {subscriptionActionLoading === "activateTrial"
                        ? SUBSCRIPTION_ONBOARDING_LABELS.activatingTrial
                        : SUBSCRIPTION_ONBOARDING_LABELS.activateFreeTrial}
                </Button>
            ) : (
                <Button
                    id={GUIDED_JOURNEY_TARGET.SUBSCRIPTION_PAID}
                    type="primary"
                    size="large"
                    loading={subscriptionActionLoading === "subscribe"}
                    disabled={!subscriptionCatalog || Boolean(catalogError) || isBusy}
                    onClick={onSubscribe}
                    className={styles.reviewCta}
                >
                    {SUBSCRIPTION_ONBOARDING_LABELS.subscribe}
                </Button>
            )}
            {activationShowsTrialCta && (
                <Text className={styles.reviewCtaHint}>{SUBSCRIPTION_PRICING_LABELS.noCreditCard}</Text>
            )}
        </div>
    );
};

/**
 * Lambda/API Gateway style: `success`, `message`, `token`, and PG fields are inside `body` (JSON string).
 * Returns the parsed inner object, or null if missing/invalid.
 */
function parseLambdaResponseBody(data) {
    if (!data || typeof data !== "object") return null;
    if (typeof data.body !== "string") return null;
    try {
        const parsed = JSON.parse(data.body);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
        return null;
    }
}

/** Prefer unwrapped Lambda body; fall back to axios data object. */
function unwrapSubscriptionResponse(data) {
    const fromBody = parseLambdaResponseBody(data);
    if (fromBody) return fromBody;
    if (data && typeof data === "object") return data;
    return null;
}

/**
 * From manageSubscription response: Cashfree PG fields `payment_session_id` and `order_id` (snake_case).
 * Accepts camelCase aliases only when reading nested API payloads.
 */
function extractCashfreePaymentContext(data) {
    if (!data || typeof data !== "object") return null;
    const inner = parseLambdaResponseBody(data);
    const candidates = inner
        ? [inner, inner.data, inner.data?.data]
        : [data, data.data, data.data?.data];
    const filtered = candidates.filter(
        (x) => x && typeof x === "object" && !Array.isArray(x)
    );
    let payment_session_id = null;
    let order_id = null;
    for (const r of filtered) {
        if (!payment_session_id) {
            payment_session_id = r.payment_session_id ?? r.paymentSessionId ?? null;
        }
        if (!order_id) {
            order_id = r.order_id ?? r.orderId ?? null;
        }
    }
    if (!payment_session_id) return null;
    return { payment_session_id, order_id };
}

const PricingLink = () => (
  <a href={SUBSCRIPTION_CONFIG.LABELS.PRICING.url} target="_blank" rel="noopener noreferrer">
    {SUBSCRIPTION_CONFIG.LABELS.PRICING.linkLabel}
  </a>
);

const SubscriptionLinks = () => (
    <div className={styles.subscriptionLinks}>
        <a href={SUBSCRIPTION_CONFIG.LABELS.PRICING.url} target="_blank" rel="noopener noreferrer">
            {SUBSCRIPTION_CONFIG.LABELS.PRICING.planDetailsLabel}
            <ExportOutlined className={styles.subscriptionLinkIcon} aria-hidden="true" />
        </a>
        <span className={styles.subscriptionLinksDivider} aria-hidden="true">•</span>
        <a href={SUBSCRIPTION_CONFIG.LABELS.PRICING.supportUrl} target="_blank" rel="noopener noreferrer">
            {SUBSCRIPTION_CONFIG.LABELS.PRICING.supportLabel}
            <ExportOutlined className={styles.subscriptionLinkIcon} aria-hidden="true" />
        </a>
    </div>
);

const Subscription = () => {
    const { message } = AntApp.useApp();
    const {
        token,
        decodeToken,
        setToken,
        subscriptionCatalog,
        subscriptionCatalogError,
        refreshSubscriptionCatalog,
    } = useAuth();
    const decodedToken = useMemo(() => decodeToken(token), [token, decodeToken]);
    const catalogError = subscriptionCatalogError?.message || "";

    const hubProfiles = useMemo(
        () => getHubProfiles(decodedToken),
        [decodedToken]
    );

    const [selectedHubId, setSelectedHubId] = useState("");
    const [renewModalOpen, setRenewModalOpen] = useState(false);
    const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
    const [downgradeModalOpen, setDowngradeModalOpen] = useState(false);
    const [renewSelectedPlan, setRenewSelectedPlan] = useState("");
    const [renewSelectedValidity, setRenewSelectedValidity] = useState(VALIDITY.MONTHLY);
    const [upgradeSelectedPlan, setUpgradeSelectedPlan] = useState("");
    const [upgradeSelectedValidity, setUpgradeSelectedValidity] = useState(VALIDITY.MONTHLY);
    const [downgradeSelectedPlan, setDowngradeSelectedPlan] = useState("");
    /** null | 'renew' | 'upgrade' | 'downgrade' | 'cancel' */
    const [subscriptionActionLoading, setSubscriptionActionLoading] = useState(null);
    const [pricingCycle, setPricingCycle] = useState("monthly");
    const [activationSelectedPlan, setActivationSelectedPlan] = useState("");

    const catalogPlans = useMemo(
        () => (subscriptionCatalog?.plans || [])
            .filter((plan) => plan.enabled)
            .sort((left, right) => Number(left.hierarchy) - Number(right.hierarchy)),
        [subscriptionCatalog]
    );
    const catalogPaidPlans = useMemo(
        () => catalogPlans
            .filter((plan) => Number(plan.monthlyPrice) > 0)
            .map((plan) => plan.id),
        [catalogPlans]
    );
    const catalogTrialPlans = useMemo(
        () => catalogPlans.filter((plan) => plan.trial?.enabled === true),
        [catalogPlans]
    );
    const catalogValidityOptions = useMemo(
        () => (subscriptionCatalog?.billingCycles || []).map((cycle) => ({
            value: cycle.id,
            label: cycle.label,
            discountPercent: cycle.discountPercent || null,
            months: cycle.months,
        })),
        [subscriptionCatalog]
    );
    const billingCycleById = useMemo(
        () => new Map((subscriptionCatalog?.billingCycles || []).map((cycle) => [cycle.id, cycle])),
        [subscriptionCatalog]
    );
    const catalogPlanById = useMemo(
        () => new Map(catalogPlans.map((plan) => [plan.id, plan])),
        [catalogPlans]
    );
    const monthlyCycle = billingCycleById.get(VALIDITY.MONTHLY) || subscriptionCatalog?.billingCycles?.[0];
    const getPlanValidityOptions = useCallback(
        (planId) => {
            const plan = catalogPlans.find((item) => item.id === planId);
            if (!plan || !subscriptionCatalog) return [];
            const cycleIds = getPlanBillingCycleIds(subscriptionCatalog, plan);
            return catalogValidityOptions.filter((option) => cycleIds.includes(option.value));
        },
        [catalogPlans, catalogValidityOptions, subscriptionCatalog]
    );
    const getCatalogValidityLabel = useCallback(
        (value) => catalogValidityOptions.find((option) => option.value === value)?.label || value,
        [catalogValidityOptions]
    );
    useEffect(() => {
        const defaultValidity = catalogValidityOptions[0]?.value;
        if (!defaultValidity) return;
        const renewOptions = getPlanValidityOptions(renewSelectedPlan);
        const upgradeOptionsForPlan = getPlanValidityOptions(upgradeSelectedPlan);
        const renewDefault = renewOptions[0]?.value || defaultValidity;
        const upgradeDefault = upgradeOptionsForPlan[0]?.value || defaultValidity;
        if (renewOptions.length > 0 && !renewOptions.some((option) => option.value === renewSelectedValidity)) {
            setRenewSelectedValidity(renewDefault);
        }
        if (upgradeOptionsForPlan.length > 0 && !upgradeOptionsForPlan.some((option) => option.value === upgradeSelectedValidity)) {
            setUpgradeSelectedValidity(upgradeDefault);
        }
    }, [
        catalogValidityOptions,
        getPlanValidityOptions,
        renewSelectedPlan,
        renewSelectedValidity,
        upgradeSelectedPlan,
        upgradeSelectedValidity,
    ]);
    useEffect(() => {
        if (!catalogValidityOptions.some((option) => option.value === pricingCycle)) {
            setPricingCycle(catalogValidityOptions[0]?.value || "monthly");
        }
    }, [catalogValidityOptions, pricingCycle]);
    useEffect(() => {
        if (!selectedHubId && hubProfiles.length > 0) {
            setSelectedHubId(hubProfiles[0]?.PK ?? "");
        }
    }, [hubProfiles, selectedHubId]);

    const handleActivationPlanSelect = useCallback((planId) => {
        setActivationSelectedPlan(planId);
        const plan = catalogPlanById.get(planId);
        if (!plan || !subscriptionCatalog) return;
        if (!planSupportsBillingCycle(subscriptionCatalog, plan, pricingCycle)) {
            const supportedCycle = getPlanBillingCycleIds(subscriptionCatalog, plan)[0];
            if (supportedCycle) setPricingCycle(supportedCycle);
        }
    }, [catalogPlanById, pricingCycle, subscriptionCatalog]);

    const selectedSubscription = useMemo(
        () => getSubscriptionForHub(decodedToken, selectedHubId),
        [decodedToken, selectedHubId]
    );

    const progressState = useMemo(() => {
        if (!selectedSubscription) {
          return { percent: 0, strokeColor: "#52c41a", remainingDays: 0 };
        }

        return getProgressBarState(
          selectedSubscription.StartDate,
          selectedSubscription.ExpiryDate
        );

    }, [selectedSubscription]);
      
    const { percent, strokeColor, remainingDays } = progressState;

    const currentPlan = (selectedSubscription?.Plan || "").toLowerCase();
    const upgradeOptions = useMemo(
        () => getUpgradeOptions(currentPlan, catalogPaidPlans),
        [currentPlan, catalogPaidPlans]
    );
    const downgradeOptions = useMemo(
        () => getDowngradeOptions(currentPlan, catalogPaidPlans),
        [currentPlan, catalogPaidPlans]
    );
    const isTrial = String(selectedSubscription?.SubscriptionStatus || "").toLowerCase() === "trial_active";
    const showUpgradeOptions = shouldShowUpgradeOptions(isTrial, remainingDays);

    useEffect(() => {
        setDowngradeSelectedPlan(downgradeOptions[0] || "");
    }, [downgradeOptions]);

    const handleRenewClick = () => {
        setRenewModalOpen(true);
        setRenewSelectedValidity(VALIDITY.MONTHLY);
        if (isTrial) {
            setRenewSelectedPlan(catalogPaidPlans[0] || "");
        } else {
            setRenewSelectedPlan(currentPlan);
        }
    };

    const isSubscriptionBusy = subscriptionActionLoading !== null;

    const handleUpgradeClick = () => {
        if (isUpgradeDisabled(currentPlan, catalogPaidPlans)) return;
        setUpgradeSelectedValidity(VALIDITY.MONTHLY);
        setUpgradeSelectedPlan(
            isTrial
                ? catalogPaidPlans[0] || ""
                : upgradeOptions[0] || ""
        );
        setUpgradeModalOpen(true);
    };

    const handleDowngradeClick = () => {
        if (isDowngradeDisabled(currentPlan, catalogPaidPlans)) return;
        setDowngradeSelectedPlan(downgradeOptions[0] || "");
        setDowngradeModalOpen(true);
    };

    const selectedHubProfile = useMemo(
        () => hubProfiles.find((p) => p?.PK === selectedHubId) || null,
        [hubProfiles, selectedHubId]
    );
    const currentPlanPresentation = useMemo(
        () => buildProfileSubscriptionSummary(subscriptionCatalog, selectedHubProfile),
        [selectedHubProfile, subscriptionCatalog]
    );
    const selectedSubscriptionState = useMemo(
        () => deriveHubSubscriptionState(selectedHubProfile),
        [selectedHubProfile]
    );

    const refreshCatalogAfterSubscriptionChange = useCallback(() => (
        refreshSubscriptionCatalog({ force: true }).catch(() => undefined)
    ), [refreshSubscriptionCatalog]);

    const applySubscriptionToken = useCallback(async (nextToken) => {
        if (nextToken) {
            setToken(nextToken);
        }
        await refreshCatalogAfterSubscriptionChange();
    }, [setToken, refreshCatalogAfterSubscriptionChange]);
    const selectedHubIsOwner = Boolean(selectedSubscription?.IsOwner);
    const selectedActivationPlan = catalogPlanById.get(activationSelectedPlan) || catalogPlans[0];
    const accountTrialAvailable = selectedHubIsOwner && selectedHubProfile?.TrialUsed !== true;
    const primaryTrialPlan = catalogTrialPlans[0] || null;
    const trialDays = Number(selectedActivationPlan?.trial?.days || primaryTrialPlan?.trial?.days || 0);
    const trialIsAvailable = accountTrialAvailable && Boolean(primaryTrialPlan);
    const activationShowsTrialCta = accountTrialAvailable &&
        Boolean(selectedActivationPlan?.trial?.enabled) &&
        isFreeTrialPlan(selectedActivationPlan);
    const isActivationFlow = selectedSubscriptionState === SUBSCRIPTION_STATE.ACTIVATION_REQUIRED;

    useEffect(() => {
        const defaultPlanId = resolveActivationDefaultPlanId({
            catalogPlans,
            catalogTrialPlans,
            catalogPaidPlans,
            accountTrialAvailable: trialIsAvailable,
        });
        if (!catalogPlans.some((plan) => plan.id === activationSelectedPlan)) {
            setActivationSelectedPlan(defaultPlanId);
        }
    }, [
        activationSelectedPlan,
        catalogPaidPlans,
        catalogPlans,
        catalogTrialPlans,
        trialIsAvailable,
    ]);

    useEffect(() => {
        if (selectedHubProfile?.TrialUsed !== true) return;
        if (!catalogTrialPlans.some((plan) => plan.id === activationSelectedPlan)) return;
        const paidDefault = catalogPaidPlans[0] || "";
        if (paidDefault) setActivationSelectedPlan(paidDefault);
    }, [
        activationSelectedPlan,
        catalogPaidPlans,
        catalogTrialPlans,
        selectedHubProfile?.TrialUsed,
    ]);

    const renewModalPlans = useMemo(() => {
        const planIds = isTrial ? catalogPaidPlans : [currentPlan];
        return planIds.map((planId) => catalogPlanById.get(planId)).filter(Boolean);
    }, [isTrial, catalogPaidPlans, currentPlan, catalogPlanById]);
    const upgradeModalPlans = useMemo(
        () => upgradeOptions.map((planId) => catalogPlanById.get(planId)).filter(Boolean),
        [upgradeOptions, catalogPlanById]
    );
    const downgradeModalPlans = useMemo(
        () => downgradeOptions.map((planId) => catalogPlanById.get(planId)).filter(Boolean),
        [downgradeOptions, catalogPlanById]
    );
    const handleRenewCycleChange = useCallback((cycleId) => {
        setRenewSelectedValidity(cycleId);
        const supportingPlan = renewModalPlans.find((plan) => (
            subscriptionCatalog && planSupportsBillingCycle(subscriptionCatalog, plan, cycleId)
        ));
        if (supportingPlan) setRenewSelectedPlan(supportingPlan.id);
    }, [renewModalPlans, subscriptionCatalog]);
    const handleUpgradeCycleChange = useCallback((cycleId) => {
        setUpgradeSelectedValidity(cycleId);
        const supportingPlan = upgradeModalPlans.find((plan) => (
            subscriptionCatalog && planSupportsBillingCycle(subscriptionCatalog, plan, cycleId)
        ));
        if (supportingPlan) setUpgradeSelectedPlan(supportingPlan.id);
    }, [upgradeModalPlans, subscriptionCatalog]);

    /**
     * After Cashfree checkout, ask backend to validate payment and optionally issue a new JWT.
     * @param {{ payment_session_id: string, order_id?: string|null }} paymentContext — PG snake_case fields
     */
    const validatePaymentAfterCheckout = useCallback(
        async (paymentContext, closeModal) => {
            const { payment_session_id, order_id } = paymentContext || {};
            if (!token || !selectedHubProfile) return;
            if (!payment_session_id) {
                message.error(SUBSCRIPTION_CONFIG.LABELS.TOASTS.missingPaymentSession);
                return;
            }
            try {
                const response = await axios.post(
                    dbUserAndHub,
                    encryptUserAndHubPayload({
                        token,
                        operationType: "manageSubscription",
                        action: "validatePayment",
                        hubId: selectedHubProfile.PK,
                        payment_session_id,
                        ...(order_id && { order_id }),
                    }),
                    { headers: { "Content-Type": "application/json" } }
                );

                const rawResponse = unwrapSubscriptionResponse(response.data);
                if (!rawResponse) {
                    message.error(
                        "Invalid payment verification response from server."
                    );
                    closeModal?.();
                    return;
                }
                const success = rawResponse.success === true;
                const explicitFailure = rawResponse.success === false;
                const newToken = rawResponse.token ?? null;

                if (explicitFailure) {
                    message.error(
                        rawResponse?.message ||
                            SUBSCRIPTION_CONFIG.LABELS.TOASTS.paymentCouldNotVerify
                    );
                    closeModal?.();
                    return;
                }

                if (success && newToken) {
                    await applySubscriptionToken(newToken);
                    message.success(
                        rawResponse?.message ||
                            SUBSCRIPTION_CONFIG.LABELS.TOASTS.paymentVerifiedUpdated
                    );
                    closeModal?.();
                    return;
                }

                if (newToken) {
                    await applySubscriptionToken(newToken);
                    message.success(
                        rawResponse?.message ||
                            SUBSCRIPTION_CONFIG.LABELS.TOASTS.paymentVerifiedYourUpdated
                    );
                    closeModal?.();
                    return;
                }

                if (success && !newToken) {
                    message.error(
                        rawResponse?.message ||
                            "Payment could not be verified. Please try again or contact support."
                    );
                    closeModal?.();
                    return;
                }

                message.info(
                    rawResponse?.message ||
                        SUBSCRIPTION_CONFIG.LABELS.TOASTS.paymentStillConfirming
                );
                closeModal?.();
            } catch (err) {
                console.error("validatePayment error:", err);
                const errInner = parseLambdaResponseBody(err?.response?.data);
                message.error(
                    errInner?.message ||
                        err?.response?.data?.message ||
                        err?.message ||
                        SUBSCRIPTION_CONFIG.LABELS.TOASTS.couldNotConfirmPayment
                );
            }
        },
        [token, selectedHubProfile, applySubscriptionToken, message]
    );

    const executeSubscriptionAction = useCallback(
        async (subscriptionActionPayload) => {
            const { actionKey, actionName, extraPayload = {}, closeModal } = subscriptionActionPayload;
            if (!selectedHubProfile) {
                message.error(SUBSCRIPTION_CONFIG.LABELS.TOASTS.noHubProfile);
                return Promise.reject();
            }
            if (!token) {
                message.error(SUBSCRIPTION_CONFIG.LABELS.TOASTS.notSignedIn);
                return Promise.reject();
            }

            setSubscriptionActionLoading(actionKey);
            try {
                const response = await axios.post(
                    dbUserAndHub,
                    encryptUserAndHubPayload({
                        token,
                        operationType: "manageSubscription",
                        action: actionName,
                        hubId: selectedHubProfile.PK,
                        ...extraPayload,
                    }),
                    { headers: { "Content-Type": "application/json" } }
                );

                const paymentCtx = extractCashfreePaymentContext(response.data);

                if (paymentCtx?.payment_session_id) {
                    const { payment_session_id, order_id } = paymentCtx;
                    const cashfree = await getCashfree();
                    const checkoutResult = await cashfree.checkout({
                        paymentSessionId: payment_session_id,
                        redirectTarget: "_modal",
                        returnUrl: `${window.location.origin}/subscription`,
                    });
                    if (checkoutResult?.error) {
                        message.error(checkoutResult.error.message || SUBSCRIPTION_CONFIG.LABELS.TOASTS.paymentCheckoutFailed);
                    } else {
                        await validatePaymentAfterCheckout(
                            { payment_session_id, order_id },
                            closeModal
                        );
                    }
                } else {
                    const rawResponse = unwrapSubscriptionResponse(response.data) || {};
                    const { token: newToken, success, message: resultMessage } = rawResponse;
                    if (success === false) {
                        message.error(resultMessage || SUBSCRIPTION_CONFIG.LABELS.TOASTS.requestFailed);
                    } else {
                        if (newToken) {
                            await applySubscriptionToken(newToken);
                        }
                        message.success(
                            resultMessage ?? SUBSCRIPTION_CONFIG.LABELS.TOASTS.requestCompleted
                        );
                    }
                    closeModal?.();
                }
            } catch (err) {
                console.error(`Subscription action ${actionName}:`, err);
                const errInner = parseLambdaResponseBody(err?.response?.data);
                message.error(
                    errInner?.message ||
                        err?.response?.data?.message ||
                        err?.message ||
                        SUBSCRIPTION_CONFIG.LABELS.TOASTS.requestTryAgain
                );
            } finally {
                setSubscriptionActionLoading(null);
            }
        },
        [token, selectedHubProfile, validatePaymentAfterCheckout, applySubscriptionToken, message]
    );

    const handleRenewAfterConfirm = useCallback(async () => {
        const validation = validatePaidCheckoutSelection({
            catalog: subscriptionCatalog,
            catalogPaidPlans,
            catalogPlanById,
            targetPlanId: renewSelectedPlan,
            validity: renewSelectedValidity,
        });
        if (!validation.ok) {
            message.warning(SUBSCRIPTION_CONFIG.LABELS.TOASTS.selectPlan);
            return Promise.reject();
        }
        return executeSubscriptionAction({
            ...buildPaidCheckoutActionPayload({
                actionKey: "renew",
                actionName: "renewPlan",
                targetPlanId: validation.targetPlanId,
                validity: validation.validity,
            }),
            closeModal: () => setRenewModalOpen(false),
        });
    }, [
        catalogPaidPlans,
        catalogPlanById,
        executeSubscriptionAction,
        message,
        renewSelectedPlan,
        renewSelectedValidity,
        subscriptionCatalog,
    ]);

    const handleUpgradeAfterConfirm = useCallback(async () => {
        const validation = validatePaidCheckoutSelection({
            catalog: subscriptionCatalog,
            catalogPaidPlans,
            catalogPlanById,
            targetPlanId: upgradeSelectedPlan,
            validity: upgradeSelectedValidity,
        });
        if (!validation.ok) {
            message.warning(SUBSCRIPTION_CONFIG.LABELS.TOASTS.selectPlan);
            return Promise.reject();
        }
        return executeSubscriptionAction({
            ...buildPaidCheckoutActionPayload({
                actionKey: "upgrade",
                actionName: "upgradePlan",
                targetPlanId: validation.targetPlanId,
                validity: validation.validity,
            }),
            closeModal: () => setUpgradeModalOpen(false),
        });
    }, [
        catalogPaidPlans,
        catalogPlanById,
        executeSubscriptionAction,
        message,
        subscriptionCatalog,
        upgradeSelectedPlan,
        upgradeSelectedValidity,
    ]);

    const handleDowngradeAfterConfirm = useCallback(async () => {
        if (isDowngradeDisabled(currentPlan, catalogPaidPlans) || !downgradeSelectedPlan) {
            message.warning(SUBSCRIPTION_CONFIG.LABELS.TOASTS.selectPlanDowngrade);
            return Promise.reject();
        }
        return executeSubscriptionAction({
            ...buildDowngradeActionPayload(downgradeSelectedPlan),
            closeModal: () => setDowngradeModalOpen(false),
        });
    }, [executeSubscriptionAction, currentPlan, catalogPaidPlans, downgradeSelectedPlan, message]);

    const handleCancelAfterConfirm = useCallback(async () => {
        return executeSubscriptionAction(buildCancelActionPayload());
    }, [executeSubscriptionAction]);

    const handleActivateTrial = useCallback(async (planIdOverride) => {
        const targetPlanId = planIdOverride || activationSelectedPlan;
        const validation = validateActivateTrialSelection({
            catalogPlanById,
            targetPlanId,
            accountTrialAvailable,
        });
        if (!validation.ok) return;
        if (planIdOverride) setActivationSelectedPlan(planIdOverride);
        return executeSubscriptionAction(buildActivateTrialActionPayload(validation.targetPlanId));
    }, [
        accountTrialAvailable,
        activationSelectedPlan,
        catalogPlanById,
        executeSubscriptionAction,
    ]);

    const handleSubscribe = useCallback(async () => {
        const validation = validatePaidCheckoutSelection({
            catalog: subscriptionCatalog,
            catalogPaidPlans,
            catalogPlanById,
            targetPlanId: activationSelectedPlan || catalogPaidPlans[0],
            validity: pricingCycle,
        });
        if (!validation.ok) {
            message.warning(
                validation.reason === "NO_PAID_PLAN"
                    ? SUBSCRIPTION_ONBOARDING_LABELS.noPaidPlans
                    : SUBSCRIPTION_CONFIG.LABELS.TOASTS.selectPlan
            );
            return;
        }
        return executeSubscriptionAction(buildPaidCheckoutActionPayload({
            actionKey: "subscribe",
            actionName: "subscribePlan",
            targetPlanId: validation.targetPlanId,
            validity: validation.validity,
        }));
    }, [
        activationSelectedPlan,
        catalogPaidPlans,
        catalogPlanById,
        executeSubscriptionAction,
        message,
        pricingCycle,
        subscriptionCatalog,
    ]);

    if (!decodedToken) {
        return (
        <div className={styles.loadingWrap}>
            <Spin size="large" />
            <p className={styles.loadingText}>{SUBSCRIPTION_CONFIG.LABELS.LOADING.text}</p>
        </div>
        );
    }

    return (
        <PageContainer>
        {catalogError && (
            <Alert
                type="error"
                showIcon
                message={SUBSCRIPTION_CONFIG.LABELS.CATALOG.unavailable}
                className={styles.catalogAlert}
            />
        )}
        {/* Header with hub selector (same pattern as Stores.js) */}
        <div className={styles.pageHeader}>
            <div>
            <Title level={3} className={styles.pageTitle}>
                {SUBSCRIPTION_CONFIG.LABELS.PAGE.title}
            </Title>
            <Text type="secondary" className={styles.pageSubtitle}>
                {SUBSCRIPTION_CONFIG.LABELS.PAGE.subtitle}
            </Text>
            </div>
            {hubProfiles.length > 0 && (
            <Select
                value={selectedHubId || undefined}
                onChange={(value) => setSelectedHubId(value)}
                placeholder={SUBSCRIPTION_CONFIG.LABELS.PAGE.hubPlaceholder}
                className={styles.hubSelect}
                options={hubProfiles.map((profile) => ({
                value: profile?.PK,
                label: getHubDisplayName(profile),
                }))}
            />
            )}
        </div>

        {subscriptionCatalog && catalogPlans.length > 0 && isActivationFlow && (
            <section
                id={GUIDED_JOURNEY_TARGET.SUBSCRIPTION_ACTIVATION}
                className={styles.pricingSection}
            >
                <div className={styles.pricingHeader}>
                    <div>
                        <Title level={4} className={styles.pricingTitle}>{SUBSCRIPTION_PRICING_LABELS.title}</Title>
                        <Text className={styles.pricingSubtitle}>{SUBSCRIPTION_PRICING_LABELS.subtitle}</Text>
                    </div>
                </div>
                {isActivationFlow && trialIsAvailable && primaryTrialPlan && selectedHubIsOwner && (
                    <TrialHighlightBanner
                        plan={primaryTrialPlan}
                        onActivateTrial={() => handleActivateTrial(primaryTrialPlan.id)}
                        loading={subscriptionActionLoading === "activateTrial"}
                        disabled={!subscriptionCatalog || Boolean(catalogError) || isSubscriptionBusy}
                    />
                )}
                <CompactPricingPicker
                    catalog={subscriptionCatalog}
                    plans={catalogPlans}
                    cycle={pricingCycle}
                    onCycleChange={setPricingCycle}
                    cycleOptions={catalogValidityOptions}
                    billingCycleById={billingCycleById}
                    selectedPlanId={isActivationFlow ? activationSelectedPlan : currentPlan}
                    onPlanSelect={isActivationFlow && selectedHubIsOwner ? handleActivationPlanSelect : undefined}
                    currentPlanId={currentPlan}
                    showTrialHighlight={accountTrialAvailable}
                    selectable={isActivationFlow && selectedHubIsOwner}
                    restrictCyclesToSelectedPlan={isActivationFlow && selectedHubIsOwner}
                    disabled={isSubscriptionBusy}
                />
                {isActivationFlow && (
                    <>
                        {!selectedHubIsOwner ? (
                            <Alert
                                type="info"
                                showIcon
                                message={SUBSCRIPTION_ONBOARDING_LABELS.staffReadOnly}
                                className={styles.activationAlert}
                            />
                        ) : selectedHubIsOwner && !trialIsAvailable && selectedHubProfile?.TrialUsed === true ? (
                            <Alert
                                type="warning"
                                showIcon
                                message={SUBSCRIPTION_ONBOARDING_LABELS.trialUnavailable}
                                className={styles.activationAlert}
                            />
                        ) : null}
                        {selectedHubIsOwner && (
                            <ActivationReviewSection
                                selectedPlan={selectedActivationPlan}
                                cycle={pricingCycle}
                                catalog={subscriptionCatalog}
                                billingCycleById={billingCycleById}
                                activationShowsTrialCta={activationShowsTrialCta}
                                trialDays={trialDays}
                                isBusy={isSubscriptionBusy}
                                catalogError={catalogError}
                                subscriptionCatalog={subscriptionCatalog}
                                onActivateTrial={handleActivateTrial}
                                onSubscribe={handleSubscribe}
                                subscriptionActionLoading={subscriptionActionLoading}
                            />
                        )}
                    </>
                )}
                <SubscriptionLinks />
            </section>
        )}

        {hubProfiles.length === 0 ? (
            <Card className={styles.contentCard}>
            <Empty
                description={SUBSCRIPTION_CONFIG.LABELS.EMPTY.noHub}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
            </Card>
        ) : !selectedHubId ? (
            <Card className={styles.contentCard}>
            <Empty description={SUBSCRIPTION_CONFIG.LABELS.EMPTY.selectHub} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </Card>
        ) : !selectedSubscription ? (
            <Card className={styles.contentCard}>
            <Empty
                description={SUBSCRIPTION_CONFIG.LABELS.EMPTY.noSubscriptionData}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
            </Card>
        ) : selectedSubscriptionState === SUBSCRIPTION_STATE.ACTIVATION_REQUIRED ? (
            null
        ) : (
            <>
            <div className={styles.cardsRow}>
                <Card className={`${styles.infoCard} ${styles.planCard}`}>
                <Text className={styles.cardLabel}>{SUBSCRIPTION_CONFIG.LABELS.CARDS.currentPlan}</Text>
                <div className={styles.currentPlanValueWrap}>
                    <div className={styles.cardValue}>
                        {currentPlanPresentation?.planName
                            || formatPlanLabel(currentPlan)
                            || SUBSCRIPTION_CONFIG.LABELS.CARDS.planValueFallback}
                    </div>
                    {isTrial && (
                        <span className={styles.trialActiveChip}>
                            {currentPlanPresentation.trialDays
                                ? SUBSCRIPTION_PROFILE_LABELS.trialBadgeDays(currentPlanPresentation.trialDays)
                                : SUBSCRIPTION_PROFILE_LABELS.trialBadge}
                        </span>
                    )}
                </div>
                </Card>
                <Card className={`${styles.infoCard} ${styles.lastAccessCard}`}>
                <Text className={styles.cardLabel}>{SUBSCRIPTION_CONFIG.LABELS.CARDS.lastAccessed}</Text>
                <div className={styles.cardValueSmall}>
                    {formatLastAccessDate(selectedSubscription.LastAccessedOn)}
                </div>
                </Card>
                <Card className={`${styles.infoCard} ${styles.startCard}`}>
                <Text className={styles.cardLabel}>{SUBSCRIPTION_CONFIG.LABELS.CARDS.startDate}</Text>
                <div className={styles.cardValue}>
                    {formatSubscriptionDate(selectedSubscription.StartDate)}
                </div>
                </Card>
                <Card className={`${styles.infoCard} ${styles.expiryCard}`}>
                    <Text className={styles.cardLabel}>{SUBSCRIPTION_CONFIG.LABELS.CARDS.expiryDate}</Text>

                    <div className={styles.cardValue}>
                        {formatSubscriptionDate(selectedSubscription.ExpiryDate)}
                    </div>

                    <div style={{ marginTop: 12 }}>
                        <Progress
                            percent={percent}
                            strokeColor={strokeColor}
                            showInfo={false}
                            size="small"
                        />
                    </div>

                    <div className={styles.remainingDays}>
                        {formatSubscriptionRemainingDaysLabel(remainingDays)}
                    </div>
                </Card>
            </div>

            {selectedHubIsOwner ? (
            <Card className={styles.actionsCard} title={SUBSCRIPTION_CONFIG.LABELS.ACTIONS.cardTitle}>
                <Space wrap size="middle" align="start">
                <Button
                    type="primary"
                    icon={<MdOutlineAutoMode />}
                    onClick={handleRenewClick}
                    disabled={!subscriptionCatalog || Boolean(catalogError)}
                    className={styles.actionButton}
                >
                    {SUBSCRIPTION_CONFIG.LABELS.ACTIONS.renew}
                </Button>
                <Button
                    icon={<CrownOutlined />}
                    onClick={handleUpgradeClick}
                    disabled={Boolean(catalogError) || isUpgradeDisabled(currentPlan, catalogPaidPlans)}
                    className={styles.actionButton}
                >
                    {SUBSCRIPTION_CONFIG.LABELS.ACTIONS.upgradePlan}
                </Button>
                {isDowngradeDisabled(currentPlan, catalogPaidPlans) ? (
                    <Button
                        disabled
                        title={SUBSCRIPTION_CONFIG.LABELS.DOWNGRADE.disabledTitle}
                        icon={<ArrowDownOutlined />}
                        className={styles.actionButton}
                    >
                        {SUBSCRIPTION_CONFIG.LABELS.ACTIONS.downgrade}
                    </Button>
                ) : (
                    <Button
                        icon={<ArrowDownOutlined />}
                        onClick={handleDowngradeClick}
                        disabled={isSubscriptionBusy}
                        className={styles.actionButton}
                    >
                        {SUBSCRIPTION_CONFIG.LABELS.ACTIONS.downgrade}
                    </Button>
                )}
                <Popconfirm
                    title={SUBSCRIPTION_CONFIG.LABELS.CANCEL.popconfirmTitle}
                    description={SUBSCRIPTION_CONFIG.LABELS.CANCEL.popconfirmDescription}
                    onConfirm={handleCancelAfterConfirm}
                    okText={SUBSCRIPTION_CONFIG.LABELS.CANCEL.okText}
                    okType="danger"
                    cancelText={SUBSCRIPTION_CONFIG.LABELS.CANCEL.cancelText}
                >
                    <Button
                        danger
                        icon={<StopOutlined />}
                        loading={subscriptionActionLoading === "cancel"}
                        disabled={isSubscriptionBusy}
                        className={styles.actionButton}
                    >
                        {SUBSCRIPTION_CONFIG.LABELS.ACTIONS.cancelSubscription}
                    </Button>
                </Popconfirm>
                </Space>
            </Card>
            ) : (
                <Alert
                    type="info"
                    showIcon
                    message={SUBSCRIPTION_ONBOARDING_LABELS.staffReadOnly}
                    className={styles.activationAlert}
                />
            )}

            {subscriptionCatalog && catalogPlans.length > 0 && (
            <section className={styles.pricingSection}>
                <div className={styles.pricingHeader}>
                    <div>
                        <Title level={4} className={styles.pricingTitle}>{SUBSCRIPTION_PRICING_LABELS.title}</Title>
                        <Text className={styles.pricingSubtitle}>{SUBSCRIPTION_PRICING_LABELS.subtitle}</Text>
                    </div>
                </div>
                <CompactPricingPicker
                    catalog={subscriptionCatalog}
                    plans={catalogPlans}
                    cycle={pricingCycle}
                    onCycleChange={setPricingCycle}
                    cycleOptions={catalogValidityOptions}
                    billingCycleById={billingCycleById}
                    selectedPlanId={currentPlan}
                    currentPlanId={currentPlan}
                    showTrialHighlight={accountTrialAvailable}
                    selectable={false}
                    disabled={isSubscriptionBusy}
                />
                <SubscriptionLinks />
            </section>
            )}
            
            {/* Renew Modal */}
            <Modal
                title={SUBSCRIPTION_CONFIG.LABELS.RENEW_MODAL.title}
                open={renewModalOpen}
                width={720}
                onCancel={() => !isSubscriptionBusy && setRenewModalOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setRenewModalOpen(false)} disabled={isSubscriptionBusy}>
                            {SUBSCRIPTION_CONFIG.LABELS.RENEW_MODAL.cancel}
                        </Button>
                        <Popconfirm
                            title={SUBSCRIPTION_CONFIG.LABELS.RENEW_MODAL.popconfirmTitle}
                            description={
                                isTrial ? (
                                    <>
                                        {SUBSCRIPTION_CONFIG.LABELS.RENEW_MODAL.trialDescriptionBeforePlan}
                                        <strong>{formatPlanLabel(renewSelectedPlan)}</strong> (
                                        {getCatalogValidityLabel(renewSelectedValidity)})
                                        {SUBSCRIPTION_CONFIG.LABELS.RENEW_MODAL.trialDescriptionAfterValidity}
                                    </>
                                ) : (
                                    <>
                                        {SUBSCRIPTION_CONFIG.LABELS.RENEW_MODAL.paidDescriptionBeforePlan}
                                        <strong>{formatPlanLabel(currentPlan)}</strong>
                                        {SUBSCRIPTION_CONFIG.LABELS.RENEW_MODAL.paidDescriptionMiddle}
                                        {getCatalogValidityLabel(renewSelectedValidity)})
                                        {SUBSCRIPTION_CONFIG.LABELS.RENEW_MODAL.paidDescriptionAfterValidity}
                                    </>
                                )
                            }
                            onConfirm={handleRenewAfterConfirm}
                            okText={SUBSCRIPTION_CONFIG.LABELS.RENEW_MODAL.proceedToPay}
                            cancelText={SUBSCRIPTION_CONFIG.LABELS.RENEW_MODAL.cancel}
                        >
                            <Button
                                type="primary"
                                loading={subscriptionActionLoading === "renew"}
                                disabled={isSubscriptionBusy}
                            >
                                {isTrial ? SUBSCRIPTION_CONFIG.LABELS.RENEW_MODAL.continue : SUBSCRIPTION_CONFIG.LABELS.RENEW_MODAL.renew}
                            </Button>
                        </Popconfirm>
                    </Space>
                }
                destroyOnClose
            >
                <Text type="secondary" block style={{ marginBottom: 16 }}>
                    {SUBSCRIPTION_CONFIG.LABELS.ACTIONS.compareBenefitsBeforeLink}
                    <PricingLink />
                    {SUBSCRIPTION_CONFIG.LABELS.ACTIONS.compareBenefitsAfterLink}
                </Text><br/><br/>
                {isTrial && (
                    <Text type="secondary" block style={{ marginBottom: 16 }}>
                        {SUBSCRIPTION_CONFIG.LABELS.RENEW_MODAL.trialHint}
                    </Text>
                )}
                <CompactPricingPicker
                    catalog={subscriptionCatalog}
                    plans={renewModalPlans}
                    cycle={renewSelectedValidity}
                    onCycleChange={handleRenewCycleChange}
                    cycleOptions={catalogValidityOptions}
                    billingCycleById={billingCycleById}
                    selectedPlanId={renewSelectedPlan}
                    onPlanSelect={setRenewSelectedPlan}
                    currentPlanId={currentPlan}
                    showTrialHighlight={false}
                    selectable
                    restrictCyclesToSelectedPlan
                    disabled={isSubscriptionBusy}
                />
            </Modal>

            {/* Upgrade Modal */}
            <Modal
                title={SUBSCRIPTION_CONFIG.LABELS.UPGRADE_MODAL.title}
                open={upgradeModalOpen}
                width={760}
                onCancel={() => !isSubscriptionBusy && setUpgradeModalOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setUpgradeModalOpen(false)} disabled={isSubscriptionBusy}>
                            {SUBSCRIPTION_CONFIG.LABELS.RENEW_MODAL.cancel}
                        </Button>
                        {showUpgradeOptions && (
                            <Popconfirm
                                title={SUBSCRIPTION_CONFIG.LABELS.UPGRADE_MODAL.popconfirmTitle}
                                description={
                                    <>
                                        {SUBSCRIPTION_CONFIG.LABELS.UPGRADE_MODAL.chargedPrefix}
                                        <strong>{formatPlanLabel(upgradeSelectedPlan)}</strong>
                                        {SUBSCRIPTION_CONFIG.LABELS.UPGRADE_MODAL.chargedMiddle}
                                        {getCatalogValidityLabel(upgradeSelectedValidity)}
                                        {SUBSCRIPTION_CONFIG.LABELS.UPGRADE_MODAL.chargedSuffix}
                                    </>
                                }
                                onConfirm={handleUpgradeAfterConfirm}
                                okText={SUBSCRIPTION_CONFIG.LABELS.RENEW_MODAL.proceedToPay}
                                cancelText={SUBSCRIPTION_CONFIG.LABELS.RENEW_MODAL.cancel}
                            >
                                <Button
                                    type="primary"
                                    loading={subscriptionActionLoading === "upgrade"}
                                    disabled={isSubscriptionBusy}
                                >
                                    {SUBSCRIPTION_CONFIG.LABELS.UPGRADE_MODAL.upgradeButton}
                                </Button>
                            </Popconfirm>
                        )}
                    </Space>
                }
                destroyOnClose
            >
                <Text type="secondary" block style={{ marginBottom: 16 }}>
                    {SUBSCRIPTION_CONFIG.LABELS.ACTIONS.compareBenefitsBeforeLink}
                    <PricingLink />
                    {SUBSCRIPTION_CONFIG.LABELS.ACTIONS.compareBenefitsAfterLink}
                </Text><br/><br/>
                {!showUpgradeOptions ? (
                    <Alert
                        type="info"
                        showIcon
                        message={SUBSCRIPTION_CONFIG.LABELS.UPGRADE_MODAL.alertMessage}
                        description={getUpgradeWindowBlockedDescription()}
                    />
                ) : (
                    <CompactPricingPicker
                        catalog={subscriptionCatalog}
                        plans={upgradeModalPlans}
                        cycle={upgradeSelectedValidity}
                        onCycleChange={handleUpgradeCycleChange}
                        cycleOptions={catalogValidityOptions}
                        billingCycleById={billingCycleById}
                        selectedPlanId={upgradeSelectedPlan}
                        onPlanSelect={setUpgradeSelectedPlan}
                        currentPlanId={currentPlan}
                        showTrialHighlight={false}
                        selectable
                        restrictCyclesToSelectedPlan
                        disabled={isSubscriptionBusy}
                    />
                )}
            </Modal>

            {/* Downgrade Modal */}
            <Modal
                title={SUBSCRIPTION_CONFIG.LABELS.DOWNGRADE_MODAL.title}
                open={downgradeModalOpen}
                width={640}
                onCancel={() => !isSubscriptionBusy && setDowngradeModalOpen(false)}
                footer={
                    <Space>
                        <Button
                            onClick={() => setDowngradeModalOpen(false)}
                            disabled={isSubscriptionBusy}
                        >
                            {SUBSCRIPTION_CONFIG.LABELS.RENEW_MODAL.cancel}
                        </Button>
                        <Popconfirm
                            title={SUBSCRIPTION_CONFIG.LABELS.DOWNGRADE.popconfirmTitle}
                            description={
                                <>
                                    {SUBSCRIPTION_CONFIG.LABELS.DOWNGRADE.beforePlanName}
                                    <strong>{formatPlanLabel(downgradeSelectedPlan)}</strong>
                                    {SUBSCRIPTION_CONFIG.LABELS.DOWNGRADE.afterPlanName}
                                </>
                            }
                            onConfirm={handleDowngradeAfterConfirm}
                            okText={SUBSCRIPTION_CONFIG.LABELS.DOWNGRADE.okText}
                            cancelText={SUBSCRIPTION_CONFIG.LABELS.RENEW_MODAL.cancel}
                        >
                            <Button
                                type="primary"
                                loading={subscriptionActionLoading === "downgrade"}
                                disabled={isSubscriptionBusy}
                            >
                                {SUBSCRIPTION_CONFIG.LABELS.DOWNGRADE_MODAL.downgradeButton}
                            </Button>
                        </Popconfirm>
                    </Space>
                }
                destroyOnClose
            >
                <Text type="secondary" block style={{ marginBottom: 16 }}>
                    {SUBSCRIPTION_CONFIG.LABELS.ACTIONS.compareBenefitsBeforeLink}
                    <PricingLink />
                    {SUBSCRIPTION_CONFIG.LABELS.ACTIONS.compareBenefitsAfterLink}
                </Text>
                <br />
                <br />
                <Text type="secondary" block style={{ marginBottom: 16 }}>
                    {SUBSCRIPTION_CONFIG.LABELS.DOWNGRADE_MODAL.selectPlanPrompt}
                </Text>
                <div className={styles.compactPlanGrid}>
                    {downgradeModalPlans.map((plan) => (
                        <CompactPlanCard
                            key={plan.id}
                            plan={plan}
                            cycle={monthlyCycle}
                            catalog={subscriptionCatalog}
                            currency={subscriptionCatalog.currency}
                            selected={downgradeSelectedPlan === plan.id}
                            selectable
                            onSelect={() => setDowngradeSelectedPlan(plan.id)}
                        />
                    ))}
                </div>
            </Modal>
            </>
        )}

    </PageContainer>
    );
};

export default Subscription;
